import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  MapPin,
  Phone,
  ChatText,
  Clock,
  User,
  CaretRight,
  Warning,
  CheckCircle,
  Car,
  Wrench,
  Eye,
  EyeSlash,
  Camera,
  Plus,
  X,
  SpinnerGap,
  Upload,
  ArrowRight,
} from "phosphor-react";
import { useTranslation } from "../hooks/useTranslation";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// =====================
// STATUS BADGE (with translation support)
// =====================
export function StatusBadge({ status, size = "default" }) {
  const { translateStatus } = useTranslation();
  
  const statusConfig = {
    scheduled: { className: "bg-blue-100 text-blue-700 border-blue-200" },
    en_route: { className: "bg-amber-100 text-amber-700 border-amber-200" },
    on_site: { className: "bg-purple-100 text-purple-700 border-purple-200" },
    in_progress: { className: "bg-orange-100 text-orange-700 border-orange-200" },
    completed: { className: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    canceled: { className: "bg-zinc-100 text-zinc-500 border-zinc-200" },
    cancelled: { className: "bg-zinc-100 text-zinc-500 border-zinc-200" },
  };

  const config = statusConfig[status] || { className: "bg-zinc-100 text-zinc-600" };
  const sizeClass = size === "lg" ? "px-3 py-1.5 text-sm" : "px-2 py-1 text-xs";
  const label = translateStatus(status);

  return (
    <Badge variant="outline" className={`${config.className} ${sizeClass} font-medium`}>
      {label}
    </Badge>
  );
}

// =====================
// PRIORITY BADGE (with translation support)
// =====================
export function PriorityBadge({ priority, size = "default" }) {
  const { translatePriority } = useTranslation();
  
  const priorityConfig = {
    low: { className: "bg-zinc-100 text-zinc-600 border-zinc-200" },
    normal: { className: "bg-blue-100 text-blue-700 border-blue-200" },
    high: { className: "bg-amber-100 text-amber-700 border-amber-200" },
    urgent: { className: "bg-red-100 text-red-700 border-red-200" },
  };

  const config = priorityConfig[priority] || priorityConfig.normal;
  const sizeClass = size === "lg" ? "px-3 py-1.5 text-sm" : "px-2 py-1 text-xs";
  const label = translatePriority(priority);

  return (
    <Badge variant="outline" className={`${config.className} ${sizeClass} font-medium`}>
      {priority === "urgent" && <Warning size={12} className="mr-1" />}
      {label}
    </Badge>
  );
}

// =====================
// VISIT CARD (Mobile-optimized, with translation support)
// =====================
export function TechVisitCard({ visit, onClick, showDate = false }) {
  const { t, translateServiceType } = useTranslation();
  const job = visit.job || {};
  const customer = visit.customer || {};

  const openMaps = (e) => {
    e.stopPropagation();
    const address = job.address || customer.addresses?.[0];
    if (address) {
      const query = encodeURIComponent(
        `${address.street || ""}, ${address.city || ""}, ${address.state || ""} ${address.zip || ""}`
      );
      window.open(`https://maps.google.com/?q=${query}`, "_blank");
    }
  };

  const callCustomer = (e) => {
    e.stopPropagation();
    if (customer.phone) {
      window.location.href = `tel:${customer.phone}`;
    }
  };

  const textCustomer = (e) => {
    e.stopPropagation();
    if (customer.phone) {
      window.location.href = `sms:${customer.phone}`;
    }
  };

  const address = job.address || customer.addresses?.[0];
  const addressStr = address
    ? `${address.street || ""}, ${address.city || ""}`
    : t("No address");

  return (
    <Card
      className="border-2 border-zinc-200 hover:border-[#FF6600]/50 transition-colors cursor-pointer active:bg-zinc-50"
      onClick={onClick}
      data-testid={`visit-card-${visit.visit_id}`}
    >
      <CardContent className="p-4">
        {/* Header: Time + Status */}
        <div className="flex items-start justify-between mb-3">
          <div>
            {showDate && (
              <p className="text-xs text-zinc-500 mb-1">
                {format(new Date(visit.scheduled_start), "EEE, MMM d")}
              </p>
            )}
            <div className="flex items-center gap-2">
              <Clock size={16} className="text-zinc-400" />
              <span className="font-medium">
                {format(new Date(visit.scheduled_start), "h:mm a")}
                {visit.scheduled_end && ` - ${format(new Date(visit.scheduled_end), "h:mm a")}`}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusBadge status={visit.status} />
            {job.priority && job.priority !== "normal" && (
              <PriorityBadge priority={job.priority} />
            )}
          </div>
        </div>

        {/* Job Title - dynamically translated */}
        <h3 className="font-heading font-semibold text-lg mb-1 uppercase tracking-tight">
          {t(job.title) || t("Untitled Job")}
        </h3>

        {/* Service Type - translated */}
        <p className="text-sm text-zinc-500 capitalize mb-3">
          {translateServiceType(job.service_type) || t("General")}
        </p>

        {/* Customer & Address */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <User size={16} className="text-zinc-400 flex-shrink-0" />
            <span className="font-medium">{customer.name || "No customer"}</span>
          </div>
          <button
            onClick={openMaps}
            className="flex items-center gap-2 text-sm text-[#FF6600] hover:underline"
          >
            <MapPin size={16} className="flex-shrink-0" />
            <span className="text-left">{addressStr}</span>
            <CaretRight size={14} />
          </button>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2 pt-3 border-t border-zinc-100">
          {customer.phone && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={callCustomer}
                className="flex-1 border-2"
              >
                <Phone size={16} className="mr-1" />
                {t("Call")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={textCustomer}
                className="flex-1 border-2"
              >
                <ChatText size={16} className="mr-1" />
                {t("Text")}
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={openMaps}
            className="flex-1 border-2"
          >
            <MapPin size={16} className="mr-1" />
            {t("Map")}
          </Button>
        </div>

        {/* Notes/Photos indicator */}
        {(visit.notes_count > 0 || visit.photos_count > 0) && (
          <div className="flex gap-3 mt-3 text-xs text-zinc-500">
            {visit.notes_count > 0 && <span>{visit.notes_count} {t("note(s)")}</span>}
            {visit.photos_count > 0 && <span>{visit.photos_count} {t("photo(s)")}</span>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =====================
// STATUS UPDATE MODAL (with translation support)
// =====================
export function StatusUpdateModal({ open, onClose, visit, onStatusUpdate }) {
  const { t, translateStatus } = useTranslation();
  const [updating, setUpdating] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");

  // Status options with translation keys
  const statuses = [
    { value: "en_route", icon: Car, descKey: "I'm on my way to the job" },
    { value: "on_site", icon: MapPin, descKey: "I've arrived at the location" },
    { value: "in_progress", icon: Wrench, descKey: "Work has started" },
    { value: "completed", icon: CheckCircle, descKey: "Job is done" },
  ];

  const handleUpdate = async () => {
    if (!selectedStatus) return;
    setUpdating(true);
    try {
      const response = await fetch(`${API}/tech/visits/${visit.visit_id}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: selectedStatus }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(t("Status updated"));
        onStatusUpdate?.(data);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("Failed to update status"));
      }
    } catch (error) {
      // Queue for offline sync
      queueOfflineAction("update_status", { visit_id: visit.visit_id, status: selectedStatus });
      toast.info(t("Status update queued for sync"));
      onClose();
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">{t("Update Status")}</DialogTitle>
          <DialogDescription>{t("Select the current status for this visit")}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-2">
            {statuses.map((status) => {
              const Icon = status.icon;
              const isSelected = selectedStatus === status.value;
              const isCurrent = visit?.status === status.value;

              return (
                <button
                  key={status.value}
                  onClick={() => setSelectedStatus(status.value)}
                  disabled={isCurrent}
                  className={`w-full flex items-center gap-4 p-4 rounded-sm border-2 transition-colors ${
                    isSelected
                      ? "border-[#FF6600] bg-[#FF6600]/5"
                      : isCurrent
                      ? "border-zinc-200 bg-zinc-50 opacity-50"
                      : "border-zinc-200 hover:border-zinc-300"
                  }`}
                >
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isSelected ? "bg-[#FF6600] text-white" : "bg-zinc-100 text-zinc-600"
                    }`}
                  >
                    <Icon size={20} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium">{translateStatus(status.value)}</p>
                    <p className="text-xs text-zinc-500">{t(status.descKey)}</p>
                  </div>
                  {isCurrent && (
                    <Badge variant="outline" className="text-xs">{t("Current")}</Badge>
                  )}
                </button>
              );
            })}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="flex-1 border-2">
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleUpdate}
            disabled={!selectedStatus || updating}
            className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
          >
            {updating ? (
              <>
                <SpinnerGap size={18} className="mr-2 animate-spin" />
                {t("Updating...")}
              </>
            ) : (
              t("Update Status")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================
// ADD NOTE MODAL (with translation support)
// =====================
export function AddNoteModal({ open, onClose, visitId, onNoteAdded, translateToEnglish }) {
  const { t, shouldTranslate } = useTranslation();
  const [noteText, setNoteText] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);

  const handleSave = async () => {
    if (!noteText.trim()) return;
    setSaving(true);

    try {
      // If translateToEnglish function is provided, translate the note first
      let finalNoteText = noteText;
      if (translateToEnglish) {
        setTranslating(true);
        try {
          finalNoteText = await translateToEnglish(noteText);
        } catch (e) {
          console.error("Translation failed, saving original:", e);
        }
        setTranslating(false);
      }

      const response = await fetch(`${API}/notes/visit/${visitId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ note_text: finalNoteText, visibility }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(t("Note added"));
        onNoteAdded?.(data);
        resetForm();
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("Failed to add note"));
      }
    } catch (error) {
      // Queue for offline sync
      queueOfflineAction("add_note", { visit_id: visitId, note_text: noteText, visibility });
      toast.info(t("Note queued for sync"));
      resetForm();
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setNoteText("");
    setVisibility("internal");
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) { resetForm(); onClose(); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">{t("Add Note")}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            <div>
              <Label>{t("Note")}</Label>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder={t("Enter your note...")}
                className="mt-1 border-2 min-h-[120px]"
                autoFocus
              />
              {shouldTranslate && translateToEnglish && (
                <p className="text-xs text-blue-600 mt-1">
                  {t("Your note will be automatically translated to English when saved.")}
                </p>
              )}
            </div>

            <div>
              <Label>{t("Visibility")}</Label>
              <Select value={visibility} onValueChange={setVisibility}>
                <SelectTrigger className="mt-1 border-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="internal">
                    <div className="flex items-center gap-2">
                      <EyeSlash size={16} />
                      {t("Internal Only")}
                    </div>
                  </SelectItem>
                  <SelectItem value="customer">
                    <div className="flex items-center gap-2">
                      <Eye size={16} />
                      {t("Visible to Customer")}
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">
                {visibility === "internal"
                  ? t("Only visible to your team")
                  : t("Customer will see this note")}
              </p>
            </div>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => { resetForm(); onClose(); }} className="flex-1 border-2">
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || translating || !noteText.trim()}
            className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
          >
            {translating ? t("Translating...") : saving ? t("Saving...") : t("Save Note")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================
// ADD PHOTO MODAL (with translation support)
// =====================
export function AddPhotoModal({ open, onClose, visitId, onPhotoAdded }) {
  const { t } = useTranslation();
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadError(null); // Clear any previous errors
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview({ file, dataUrl: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!preview?.file) return;
    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append("file", preview.file);
      formData.append("caption", caption);
      formData.append("visibility", visibility);

      const response = await fetch(`${API}/photos/visit/${visitId}`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(t("Photo uploaded"));
        onPhotoAdded?.(data);
        resetForm();
        onClose();
      } else {
        // Show the actual backend error message
        const error = await response.json();
        const errorMessage = error.detail || t("Failed to upload photo");
        setUploadError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = t("Failed to upload. Check your connection.");
      setUploadError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setPreview(null);
    setCaption("");
    setVisibility("internal");
    setUploadError(null);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) { resetForm(); onClose(); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">{t("Add Photo")}</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <div className="space-y-4">
            {!preview ? (
              <div className="border-2 border-dashed border-zinc-300 rounded-sm p-8 text-center">
                <Camera size={48} className="mx-auto mb-4 text-zinc-400" />
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="photo-input"
                />
                <label htmlFor="photo-input">
                  <Button variant="outline" className="border-2" asChild>
                    <span>
                      <Camera size={18} className="mr-2" />
                      {t("Take Photo or Choose File")}
                    </span>
                  </Button>
                </label>
              </div>
            ) : (
              <>
                <div className="relative rounded-sm overflow-hidden border-2 border-zinc-200">
                  <img src={preview.dataUrl} alt="Preview" className="w-full h-48 object-cover" />
                  <button
                    onClick={() => setPreview(null)}
                    className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <X size={16} />
                  </button>
                </div>

                <div>
                  <Label>{t("Caption (optional)")}</Label>
                  <Input
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    placeholder={t("Add a description...")}
                    className="mt-1 border-2"
                  />
                </div>

                <div>
                  <Label>{t("Visibility")}</Label>
                  <Select value={visibility} onValueChange={setVisibility}>
                    <SelectTrigger className="mt-1 border-2">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="internal">{t("Internal Only")}</SelectItem>
                      <SelectItem value="customer">{t("Visible to Customer")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* Show upload error if any */}
            {uploadError && (
              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-md">
                <Warning size={16} />
                <span>{uploadError}</span>
              </div>
            )}
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => { resetForm(); onClose(); }} className="flex-1 border-2">
            {t("Cancel")}
          </Button>
          {preview && (
            <Button
              onClick={handleUpload}
              disabled={uploading}
              className="flex-1 bg-[#FF6600] hover:bg-[#CC5200] text-white"
            >
              {uploading ? (
                <>
                  <SpinnerGap size={18} className="mr-2 animate-spin" />
                  {t("Uploading...")}
                </>
              ) : (
                <>
                  <Upload size={18} className="mr-2" />
                  {t("Upload")}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================
// COMPLETE VISIT MODAL (with translation support)
// =====================
export function CompleteVisitModal({ open, onClose, visit, onComplete }) {
  const { t } = useTranslation();
  const [completing, setCompleting] = useState(false);

  const handleComplete = async () => {
    setCompleting(true);
    try {
      const response = await fetch(`${API}/tech/visits/${visit.visit_id}/complete`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(t("Visit completed!"));
        onComplete?.(data);
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || t("Failed to complete visit"));
      }
    } catch (error) {
      queueOfflineAction("update_status", { visit_id: visit.visit_id, status: "completed" });
      toast.info(t("Completion queued for sync"));
      onClose();
    } finally {
      setCompleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">{t("Complete Visit")}</DialogTitle>
          <DialogDescription>
            {t("Confirm that all work has been completed for this visit.")}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="py-4 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
              <CheckCircle size={32} className="text-emerald-600" />
            </div>
            <p className="font-medium">{t(visit?.job?.title) || t("This Visit")}</p>
            <p className="text-sm text-zinc-500 mt-1">
              {visit?.customer?.name}
            </p>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="flex-1 border-2">
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleComplete}
            disabled={completing}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {completing ? (
              <>
                <SpinnerGap size={18} className="mr-2 animate-spin" />
                {t("Completing...")}
              </>
            ) : (
              <>
                <CheckCircle size={18} className="mr-2" />
                {t("Mark Complete")}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =====================
// EMPTY STATE
// =====================
export function TechEmptyState({ icon: Icon, title, description, action }) {
  return (
    <div className="text-center py-12">
      <div className="w-16 h-16 rounded-full bg-zinc-100 mx-auto mb-4 flex items-center justify-center">
        <Icon size={32} className="text-zinc-400" />
      </div>
      <h3 className="font-medium text-zinc-900 mb-1">{title}</h3>
      <p className="text-sm text-zinc-500 mb-4">{description}</p>
      {action}
    </div>
  );
}

// =====================
// LOADING STATE
// =====================
export function TechLoadingState({ message = "Loading..." }) {
  return (
    <div className="text-center py-12">
      <SpinnerGap size={32} className="animate-spin text-[#FF6600] mx-auto mb-4" />
      <p className="text-zinc-500">{message}</p>
    </div>
  );
}

// =====================
// OFFLINE QUEUE HELPER
// =====================
export function queueOfflineAction(action, data) {
  try {
    const queue = JSON.parse(localStorage.getItem("offlineQueue") || "[]");
    queue.push({
      local_id: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      action,
      data,
      timestamp: new Date().toISOString(),
    });
    localStorage.setItem("offlineQueue", JSON.stringify(queue));
    window.dispatchEvent(new Event("storage"));
  } catch (e) {
    console.error("Failed to queue offline action:", e);
  }
}

export async function syncOfflineQueue() {
  try {
    const queue = JSON.parse(localStorage.getItem("offlineQueue") || "[]");
    if (queue.length === 0) return { synced: 0, failed: 0 };

    const response = await fetch(`${API}/tech/sync`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ items: queue }),
    });

    if (response.ok) {
      const result = await response.json();
      // Remove successfully synced items
      const failedIds = result.results
        .filter((r) => !r.success)
        .map((r) => r.local_id);
      const remainingQueue = queue.filter((item) => failedIds.includes(item.local_id));
      localStorage.setItem("offlineQueue", JSON.stringify(remainingQueue));
      window.dispatchEvent(new Event("storage"));
      return { synced: result.synced_count, failed: failedIds.length };
    }
    return { synced: 0, failed: queue.length };
  } catch (e) {
    console.error("Sync failed:", e);
    return { synced: 0, failed: -1 };
  }
}

// Auto-sync when coming online
if (typeof window !== "undefined") {
  window.addEventListener("online", async () => {
    const result = await syncOfflineQueue();
    if (result.synced > 0) {
      toast.success(`Synced ${result.synced} offline action(s)`);
    }
  });
}

export default {
  StatusBadge,
  PriorityBadge,
  TechVisitCard,
  StatusUpdateModal,
  AddNoteModal,
  AddPhotoModal,
  CompleteVisitModal,
  TechEmptyState,
  TechLoadingState,
  queueOfflineAction,
  syncOfflineQueue,
};
